import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth, hasRole } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useFirm, FirmMark } from '@/lib/brand';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { NotificationBell } from '@/components/NotificationBell';
import { MaintenanceBanner } from '@/components/MaintenanceBanner';
import TutorialAndHelp from '@/components/TutorialAndHelp';
import { GlobalSearch } from '@/components/GlobalSearch';
import { VoiceCommandButton } from '@/components/VoiceCommandButton';
import { Badge } from '@/components/ui/badge';
import {
  Receipt, LayoutDashboard, Users2, ScrollText, ReceiptText, FileBarChart2, Wrench, UserCog, Settings as SettingsIcon,
  Shield, ShieldAlert, Plus, Menu, LogOut, ChevronDown, Languages, MessageSquareWarning, Wallet, PiggyBank, User as UserIcon,
  Activity, Landmark, CalendarClock, Ticket, Building2, X as XIcon, Sparkles, Search as SearchIcon, Bot, FileScan, TrendingUp, PlugZap,
  CalendarRange, Scale, Inbox,
} from 'lucide-react';

function NavItem({ to, icon: Icon, label, testId, onClick, badge }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      end
      data-testid={testId}
      className={({ isActive }) =>
        `sidebar-link flex items-center gap-2.5 rounded-md px-3 py-2 text-sm relative ${
          isActive
            ? 'bg-firm-navy text-white font-medium shadow-sm before:content-[""] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r before:bg-firm-gold'
            : 'text-slate-700 hover:bg-slate-100'
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate flex-1">{label}</span>
      {badge > 0 && (
        <span
          className="ml-auto shrink-0 min-w-[20px] h-[18px] px-1.5 rounded-full bg-firm-gold text-firm-navy text-[10px] font-semibold flex items-center justify-center"
          data-testid={`${testId}-badge`}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );
}

function SidebarContent({ user, t, closeMobile, firm, pendingCount, closeBadge, pendingPayBadge }) {
  return (
    <div className="flex h-full flex-col bg-white">
      <div className="px-4 py-5 border-b border-slate-200 bg-gradient-to-b from-white to-slate-50">
        <div className="flex items-center gap-2.5">
          <FirmMark size={44} />
          <div className="min-w-0 flex-1">
            <div className="font-display font-semibold text-firm-navy leading-tight truncate text-[15px]" data-testid="firm-name-display">{firm?.firm_name || 'EZ DockIT'}</div>
            <div className="text-[10px] text-firm-gold leading-tight tracking-[0.22em] uppercase mt-0.5">Practice Management</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        <NavItem to="/nuevo-recibo" icon={Plus} label={t('nav_quick_receipt')} testId="nav-quick-receipt" onClick={closeMobile} />
        {hasRole(user, 'admin', 'accountant') && (
          <NavItem to="/dashboard" icon={LayoutDashboard} label={t('nav_dashboard')} testId="nav-dashboard" onClick={closeMobile} />
        )}
        <NavItem to="/clientes" icon={Users2} label={t('nav_clients')} testId="nav-clients" onClick={closeMobile} />
        <NavItem to="/recibos" icon={ScrollText} label={t('nav_receipts')} testId="nav-receipts" onClick={closeMobile} />
        <NavItem to="/pagos-recibos" icon={ReceiptText} label={t('nav_payment_receipts')} testId="nav-payment-receipts" onClick={closeMobile} />
        {hasRole(user, 'admin') && <NavItem to="/cupones" icon={Ticket} label="Cupones" testId="nav-coupons" onClick={closeMobile} />}
        <NavItem to="/gastos" icon={Wallet} label="Gastos" testId="nav-expenses" onClick={closeMobile} />
        <NavItem to="/contabilidad" icon={PiggyBank} label="Contabilidad" testId="nav-accounting" onClick={closeMobile} />
        <NavItem to="/fideicomiso" icon={Landmark} label="Fideicomiso" testId="nav-trust" onClick={closeMobile} />
        <NavItem to="/recurrentes" icon={CalendarClock} label="Recurrentes" testId="nav-recurring" onClick={closeMobile} />
        {hasRole(user, 'admin', 'accountant') && (
          <>
            <NavItem to="/reportes" icon={FileBarChart2} label={t('nav_reports')} testId="nav-reports" onClick={closeMobile} />
            <NavItem to="/reportes/ajustes-meruit" icon={Scale} label="Ajustes Meruit" testId="nav-adjustments" onClick={closeMobile} />
            <NavItem to="/pagos-pendientes" icon={Inbox} label="Pagos por confirmar" testId="nav-pending-payments" onClick={closeMobile} badge={pendingPayBadge} />
          </>
        )}
        <NavItem to="/servicios" icon={Wrench} label={t('nav_services')} testId="nav-services" onClick={closeMobile} />

        <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-widest text-firm-navy/60 font-semibold flex items-center gap-2"><span className="h-[1px] w-3 bg-firm-gold" /> Inteligencia Artificial</div>
        <NavItem to="/asistente" icon={Bot} label="Asistente IA" testId="nav-assistant" onClick={closeMobile} />
        <NavItem to="/pulso-clientes" icon={Activity} label="Client Pulse" testId="nav-client-pulse" onClick={closeMobile} />
        <NavItem to="/prediccion-pagos" icon={TrendingUp} label="Predicción de pagos" testId="nav-payment-predictions" onClick={closeMobile} />
        <NavItem to="/meruit" icon={PlugZap} label="Integración Meruit" testId="nav-meruit" onClick={closeMobile} />
        <NavItem to="/meruit/cierre-mensual" icon={CalendarRange} label="Cierre mensual" testId="nav-monthly-close" onClick={closeMobile} badge={closeBadge} />
        <NavItem to="/analizar-documento" icon={FileScan} label="Analizar documento" testId="nav-doc-analyzer" onClick={closeMobile} />
        {hasRole(user, 'admin', 'accountant') && (
          <NavItem to="/reportes-ia" icon={Sparkles} label="Reportes IA" testId="nav-reports-ia" onClick={closeMobile} />
        )}

        {hasRole(user, 'admin') && (
          <>
            <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-widest text-firm-navy/60 font-semibold flex items-center gap-2"><span className="h-[1px] w-3 bg-firm-gold" /> Administración</div>
            <NavItem to="/usuarios" icon={UserCog} label={t('nav_users')} testId="nav-users" onClick={closeMobile} />
            <NavItem to="/solicitudes" icon={MessageSquareWarning} label="Solicitudes" testId="nav-deletion-requests" onClick={closeMobile} badge={pendingCount} />
            <NavItem to="/configuracion" icon={SettingsIcon} label={t('nav_settings')} testId="nav-settings" onClick={closeMobile} />
            <NavItem to="/tour-personalizado" icon={Sparkles} label="Tour personalizado" testId="nav-firm-tour" onClick={closeMobile} />
            <NavItem to="/auditoria" icon={Shield} label={t('nav_audit')} testId="nav-audit" onClick={closeMobile} />
            <NavItem to="/estado-sistema" icon={Activity} label="Estado del sistema" testId="nav-system-ops" onClick={closeMobile} />
          </>
        )}
        {/* Tanda 17: Super Admin console (platform-level) */}
        {user?.is_super_admin && (
          <>
            <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-widest text-amber-700 font-medium flex items-center gap-1">
              <ShieldAlert className="h-3 w-3" /> Super Admin
            </div>
            <NavItem to="/super-admin/firmas" icon={Building2} label="Firmas" testId="nav-super-admin-firms" onClick={closeMobile} />
            <NavItem to="/super-admin/tour" icon={Sparkles} label="Tour de bienvenida" testId="nav-super-admin-tour" onClick={closeMobile} />
          </>
        )}
        {!hasRole(user, 'admin') && (
          <NavItem to="/solicitudes" icon={MessageSquareWarning} label="Mis solicitudes" testId="nav-my-deletion-requests" onClick={closeMobile} />
        )}
      </nav>

      <div className="border-t border-slate-200 px-4 py-3 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 font-medium">
            {(user?.name || '?').slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-slate-900 font-medium leading-tight truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-500 leading-tight capitalize">{user?.role}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { firm } = useFirm();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  // Cmd+K / Ctrl+K to open global search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen((s) => !s);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Global voice-action handler: some voice commands fire window events.
  useEffect(() => {
    const onVoice = (e) => {
      const res = e?.detail;
      if (!res) return;
      if (res.action === 'logout') {
        try { logout?.(); } catch (_) { /* ignore */ }
      }
      if (res.action === 'open_search') {
        setSearchOpen(true);
      }
    };
    window.addEventListener('ezdockit:voice-action', onVoice);
    return () => window.removeEventListener('ezdockit:voice-action', onVoice);
  }, [logout]);

  const isAdmin = hasRole(user, 'admin');
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const fetchCount = () => {
      api.get('/deletion-requests/pending-count')
        .then((r) => { if (!cancelled) setPendingCount(r.data?.count || 0); })
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000); // refresh every 30s
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [isAdmin]);

  // Meruit MOCK mode banner — poll config to detect it
  const [mockActive, setMockActive] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const fetchMockFlag = async () => {
      try {
        const { data } = await api.get('/firm/meruit-config');
        if (!cancelled) setMockActive(!!data?.use_mock);
      } catch (_) {}
    };
    fetchMockFlag();
    const iv = setInterval(fetchMockFlag, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isAdmin]);

  // Pending payments badge (admin + accountant)
  const [pendingPayBadge, setPendingPayBadge] = useState(0);
  useEffect(() => {
    if (!hasRole(user, 'admin', 'accountant')) return;
    let cancelled = false;
    const fetch = async () => {
      try {
        const { data } = await api.get('/pending-payments?status=pending');
        if (!cancelled) setPendingPayBadge(data?.pending_count || 0);
      } catch (_) {}
    };
    fetch();
    const iv = setInterval(fetch, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);

  // Meruit close-reminder badge (admin + accountant)
  const [closeBadge, setCloseBadge] = useState(0);
  useEffect(() => {
    if (!hasRole(user, 'admin', 'accountant')) return;
    let cancelled = false;
    const fetch = async () => {
      try {
        const { data } = await api.get('/meruit/close-reminder');
        if (!cancelled) setCloseBadge(data?.needs_close ? (data?.unbilled_count || 1) : 0);
      } catch (_) {}
    };
    fetch();
    const iv = setInterval(fetch, 120000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);

  return (
    <div className="min-h-screen app-canvas">
      {mockActive && (
        <div className="w-full bg-rose-600 text-white text-center py-1.5 text-xs font-semibold tracking-wide sticky top-0 z-40" data-testid="meruit-mock-banner">
          ⚠ MODO PRUEBA — MERUIT MOCK ACTIVO · Los correos a clientes reales están BLOQUEADOS · Desactive el mock en Configuración → Meruit
        </div>
      )}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 border-r border-slate-200 z-30">
        <SidebarContent user={user} t={t} closeMobile={() => {}} firm={firm} pendingCount={pendingCount} closeBadge={closeBadge} pendingPayBadge={pendingPayBadge} />
      </aside>

      <header className="lg:pl-64 sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="h-14 px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="open-mobile-nav">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <SidebarContent user={user} t={t} closeMobile={() => setMobileOpen(false)} firm={firm} pendingCount={pendingCount} closeBadge={closeBadge} pendingPayBadge={pendingPayBadge} />
              </SheetContent>
            </Sheet>
            <div className="lg:hidden flex items-center gap-2">
              <FirmMark size={28} />
              <span className="font-display font-semibold text-slate-900">{firm?.firm_name || 'EZ DockIT'}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSearchOpen(true)}
              data-testid="topbar-open-search"
              className="hidden sm:inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 pl-2 pr-2 sm:pr-3"
              title="Buscar (⌘K)"
            >
              <SearchIcon className="h-4 w-4" />
              <span className="hidden md:inline text-xs">Buscar…</span>
              <kbd className="hidden md:inline rounded border bg-slate-100 px-1 py-0 text-[10px] text-slate-500">⌘K</kbd>
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} data-testid="topbar-open-search-mobile" className="sm:hidden">
              <SearchIcon className="h-5 w-5" />
            </Button>

            <Button size="sm" onClick={() => navigate('/nuevo-recibo')} data-testid="topbar-new-receipt" className="hidden sm:inline-flex">
              <Plus className="h-4 w-4 mr-1" /> {t('nav_quick_receipt')}
            </Button>

            <NotificationBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="language-toggle" className="gap-1">
                  <Languages className="h-4 w-4" />
                  <span className="uppercase text-xs font-medium">{lang}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLang('es')} data-testid="lang-es">Español</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLang('en')} data-testid="lang-en">English</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="user-menu" className="gap-1.5">
                  <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 text-xs font-semibold">
                    {(user?.name || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-slate-500">{user?.email}</div>
                  <Badge variant="secondary" className="mt-1.5 capitalize">{user?.role}</Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/perfil')} data-testid="menu-my-profile">
                  <UserIcon className="h-4 w-4 mr-2" /> Mi perfil
                </DropdownMenuItem>
                {hasRole(user, 'admin') && (
                  <DropdownMenuItem
                    onClick={async () => {
                      const currently = !!firm?.maintenance_mode;
                      const next = !currently;
                      if (next && !window.confirm('¿Activar modo mantenimiento? Los usuarios no-admin no podrán hacer cambios.')) return;
                      try {
                        await api.post('/admin/maintenance', { enabled: next });
                        window.location.reload();
                      } catch (e) { /* silent */ }
                    }}
                    data-testid="menu-toggle-maintenance"
                  >
                    <SettingsIcon className="h-4 w-4 mr-2" />
                    {firm?.maintenance_mode ? 'Desactivar mantenimiento' : 'Activar mantenimiento'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-rose-600">
                  <LogOut className="h-4 w-4 mr-2" /> {t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <MaintenanceBanner />

      {/* Tanda 17: Impersonation banner — active when a super admin is impersonating a firm */}
      <ImpersonationBanner />

      <main className="lg:pl-64">
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Tanda 17 Fase 5: Tutorial + AI Help floating button */}
      <TutorialAndHelp />

      {/* Global search palette (⌘K / Ctrl+K) */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Voice command floating widget */}
      <VoiceCommandButton />
    </div>
  );
}

function ImpersonationBanner() {
  const [state, setState] = useState(() => {
    try { return JSON.parse(localStorage.getItem('firmpay_impersonation') || 'null'); }
    catch { return null; }
  });
  if (!state?.active) return null;
  const exit = () => {
    try {
      const prev = state.previous_token;
      if (prev) localStorage.setItem('firmpay_token', prev);
      else localStorage.removeItem('firmpay_token');
      localStorage.removeItem('firmpay_impersonation');
    } catch {}
    window.location.href = '/super-admin/firmas';
  };
  return (
    <div className="lg:pl-64 sticky top-16 z-30 bg-amber-500 text-slate-900 px-4 py-2 flex items-center justify-between gap-2 shadow-md" data-testid="impersonation-banner">
      <div className="flex items-center gap-2 text-sm font-medium">
        <ShieldAlert className="h-4 w-4" />
        <span>Modo impersonación · <b>{state.firm_name}</b></span>
        <span className="hidden sm:inline text-xs opacity-80">(todas las acciones se auditan)</span>
      </div>
      <Button size="sm" variant="secondary" onClick={exit} className="bg-white text-slate-900 hover:bg-slate-100" data-testid="impersonation-exit-btn">
        <XIcon className="h-3.5 w-3.5 mr-1" /> Salir de impersonación
      </Button>
    </div>
  );
}
